"use client";
import type { MouseEvent, MouseEventHandler, MutableRefObject, ReactNode } from "react";
import React, { useRef } from "react";
import type { PopupAlignment, PopupFunctions } from "@/components/generic/popup/Popup.js";
import { Popup } from "@/components/generic/popup/Popup.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { useOptionalIsMobile } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";


interface PopupButtonProps {
    buttonClassName?: string;
    contentClassName?: string;
    children: ReactNode;
    popupRef?: MutableRefObject<PopupFunctions | null>;
    buttonContent: ReactNode;
    minPopupWidthPx?: number;
    darkMode?: boolean;
    alignment?: PopupAlignment;
    includeExtraSpaceOnMobile?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
}


export function PopupButton({
    buttonClassName, contentClassName, children, popupRef,
    buttonContent, minPopupWidthPx, darkMode, alignment,
    includeExtraSpaceOnMobile,
    onOpen, onClose,
}: PopupButtonProps) {
    alignment ??= "center";

    const isMobile = useOptionalIsMobile();
    const showExtraSpace = !!includeExtraSpaceOnMobile && !!isMobile;

    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const internalPopupRef = useRef<PopupFunctions | null>(null);

    const toggleVisibility: MouseEventHandler = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const popup = internalPopupRef.current;
        const button = buttonRef.current;
        if (!popup || !button)
            return;

        if (popup.isOpen()) {
            popup.close();
        } else {
            popup.open(button, alignment);
        }
    };

    return (
        <>
            <Popup
                setPopupFunctions={(elem: PopupFunctions | null) => {
                    if (popupRef) {
                        popupRef.current = elem;
                    }
                    internalPopupRef.current = elem;
                }}
                minPopupWidthPx={minPopupWidthPx}
                containerClassName="block p-1.5 max-w-sm"
                contentsClassName={cn(
                    "flex flex-col items-start w-full py-2 px-3 mb-6 text-base text-left",
                    "border border-subtle rounded-xl shadow-[0_2px_8px_rgb(0_0_0/0.3)]",
                    darkMode ? "text-body bg-surface-sunken" : "text-on-light bg-chip-strong",
                    contentClassName ?? null,
                )}
                onOpen={onOpen}
                onClose={onClose}>

                {children}
            </Popup>

            <button
                className={cn(
                    "cursor-pointer",
                    showExtraSpace ? "relative" : null,
                    buttonClassName ?? null,
                )}
                onClick={toggleVisibility}
                ref={buttonRef}>

                {buttonContent}

                {showExtraSpace && (
                    <button
                        className="absolute -left-1/2 -top-1/2 w-[200%] h-[200%]"
                        onClick={toggleVisibility} />
                )}
            </button>
        </>
    );
}
